import React from "react";
import styles from "../../../styles/AdminConsole.module.css";
import LeadRow from "./LeadRow";

/**
 * LeadTable
 *
 * Renders the scrollable table shell, the sticky header, and maps
 * memoised LeadRow components. The drag-scroll ref and handlers come
 * from useDragScroll in the parent shell so they stay on the DOM node.
 *
 * Props:
 *   scrollRef             React ref — from useDragScroll()
 *   onMouseDown           handler   — from useDragScroll()
 *   onMouseMove           handler   — from useDragScroll()
 *   onMouseUp             handler   — from useDragScroll()
 *   paginated             Lead[]
 *   isAdmin               bool
 *   canWrite              bool
 *   supplierUsers         array
 *   deleteConfirmId       string | null
 *   editingMeetingId      string | null
 *   meetingDateInput      string
 *   meetingLocationInput  string
 *   + all row-level handlers (passed straight through to LeadRow)
 *
 * Note: onDismissDelete has been removed. Admin-side delete-request
 * handling (Confirm / Dismiss) now lives in LeadsTab. This table only
 * needs onCancelDelete for the merchandiser to retract their own request.
 */
const LeadTable = React.memo(function LeadTable({
  scrollRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  paginated,
  isAdmin,
  canWrite,
  supplierUsers,
  deleteConfirmId,
  editingMeetingId,
  meetingDateInput,
  meetingLocationInput,
  onToggleField,
  onSampleChange,
  onOpenLogContact,
  onOpenSampleLog,
  onOpenEmailsPanel,
  onOpenEdit,
  onDelete,
  onApprove,
  onReject,
  onCancelDelete,
  onAssignSupplier,
  onViewContactLog,
  onSetEditingMeeting,
  onMeetingDateChange,
  onMeetingLocationChange,
  onSaveMeeting,
}) {
  const colSpan = 12 + (isAdmin ? 2 : 0) + (canWrite ? 2 : 0);

  return (
    <div
      ref={scrollRef}
      className={styles.tableScroll}
      style={{
        overflowX: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        cursor: "grab",
        userSelect: "none",
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <table className={styles.table}>
        <thead>
          <tr className={styles.tableHeadRow}>
            <th className={styles.th}>Buyer</th>
            <th className={styles.th}>Created By</th>
            <th className={styles.th}>Contact</th>
            <th className={styles.th}>Emails</th>
            <th className={styles.th}>Country</th>
            <th className={styles.th}>Fabric</th>
            <th className={styles.th}>Trade Role</th>
            <th className={styles.th}>Tryouts</th>
            <th className={styles.th}>Last Contact</th>
            <th className={styles.th}>Meeting</th>
            <th className={styles.th}>Sample</th>
            <th className={styles.th}>Converted</th>
            <th className={styles.th}>Payment</th>
            {isAdmin && <th className={styles.th}>Supplier</th>}
            {isAdmin && <th className={styles.th}>Actions</th>}
            {canWrite && <th className={styles.th}>Edit</th>}
            {canWrite && <th className={styles.th}>Delete</th>}
          </tr>
        </thead>
        <tbody>
          {paginated.length === 0 && (
            <tr>
              <td
                className={styles.td}
                colSpan={colSpan + 1}
                style={{ textAlign: "center", opacity: 0.5 }}
              >
                No leads found.
              </td>
            </tr>
          )}
          {paginated.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              isAdmin={isAdmin}
              canWrite={canWrite}
              writeAllowed={canWrite && lead.status === "approved"}
              supplierUsers={supplierUsers}
              deleteConfirmId={deleteConfirmId}
              editingMeetingId={editingMeetingId}
              meetingDateInput={meetingDateInput}
              meetingLocationInput={meetingLocationInput}
              onToggleField={onToggleField}
              onSampleChange={onSampleChange}
              onOpenLogContact={onOpenLogContact}
              onOpenSampleLog={onOpenSampleLog}
              onOpenEmailsPanel={onOpenEmailsPanel}
              onOpenEdit={onOpenEdit}
              onDelete={onDelete}
              onApprove={onApprove}
              onReject={onReject}
              onCancelDelete={onCancelDelete}
              onAssignSupplier={onAssignSupplier}
              onViewContactLog={onViewContactLog}
              onSetEditingMeeting={onSetEditingMeeting}
              onMeetingDateChange={onMeetingDateChange}
              onMeetingLocationChange={onMeetingLocationChange}
              onSaveMeeting={onSaveMeeting}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default LeadTable;
